import { Location } from '@angular/common';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { FeatureDetectionService } from '../core/feature-detection.service';
import { StorageService } from '../core/storage.service';
import { TrackingService } from '../core/tracking.service';
import { Favorite, Favorites } from './favorites';

@Injectable({
    providedIn: 'root',
})
export class FavoritesService implements OnDestroy {
    private readonly storageKeyFavorite = 'favorites';
    private readonly storageTimeInDays = 30;
    private readonly unsubscribe = new Subject<void>();
    private readonly favoritesBs$: BehaviorSubject<Favorites> = new BehaviorSubject(
        this.storageService.get<Favorites>(this.storageKeyFavorite) || []
    );
    public readonly favorites$ = this.favoritesBs$.asObservable();

    constructor(
        private readonly storageService: StorageService,
        private readonly featureDetectionService: FeatureDetectionService,
        private readonly trackingService: TrackingService,
        private readonly location: Location,
        private readonly router: Router
    ) {
        if (this.featureDetectionService.isServer() || this.featureDetectionService.isAudit()) {
            return;
        }
        this.storageService
            .observe<Favorites>(this.storageKeyFavorite)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((value) => {
                this.syncTabs(value);
            });
    }

    public add(favorite: Favorite): void {
        this.updateFavorites({ favorites: [...this.favoritesBs$.value, favorite] });
        this.trackFavorite('add', favorite);
        this.setFavoriteInUrl(favorite);
    }

    public remove(id: Favorite['id']): void {
        this.updateFavorites({
            favorites: this.favoritesBs$.value.filter((favorite) => {
                const isFavoriteToBeRemoved = favorite.id === id;
                if (isFavoriteToBeRemoved) {
                    this.trackFavorite('remove', favorite);
                }
                return !isFavoriteToBeRemoved;
            }),
        });
        this.setFavoriteInUrl();
    }

    private trackFavorite(eventLabel: 'add' | 'remove', favorite: Favorite): void {
        const { id, make, model, series } = favorite;
        const favoriteToTrack = {
            id,
            make,
            model,
            series,
            bodyType: this.findBodyType(favorite.bodyTypes),
        };
        this.trackingService.trackFavorite(eventLabel, favoriteToTrack);
    }

    private findBodyType(bodyTypes: Favorite['bodyTypes']): string | undefined {
        const typesToInclude = ['Hatchback', 'Lille bil', 'SUV', 'Sedan', 'Stationcar'];
        return bodyTypes?.find((type) => typesToInclude.includes(type));
    }

    // This is for "Meta Pixel" tracking.
    // We simply add the properties of marked vehicle in the url,
    // but do not need to worry about persistency or anything else
    private setFavoriteInUrl({ model, bodyTypes }: Partial<Favorite> = {}): void {
        const type = this.findBodyType(bodyTypes);
        const serializedUrl = this.router.serializeUrl(
            this.router.createUrlTree([], {
                queryParams: {
                    ...this.router.parseUrl(this.location.path()).queryParams,
                    favor: model,
                    type,
                },
                queryParamsHandling: 'merge',
                preserveFragment: true,
            })
        );
        this.location.replaceState(serializedUrl, undefined, this.location.getState());
    }

    private syncTabs(storedFavorites?: Favorites) {
        this.updateFavorites({
            favorites: storedFavorites || [],
            setStorage: false,
        });
    }

    // To avoid multiple tabs pinging back and forth when syncing, we can set favorites without setting storage
    private updateFavorites({ favorites, setStorage = true }: { favorites: Favorites; setStorage?: boolean }): void {
        this.favoritesBs$.next(favorites);
        if (setStorage) {
            this.setFavoriteStorage(favorites);
        }
    }

    public setFavoriteStorage(favorites: Favorites): void {
        this.storageService.set(this.storageKeyFavorite, favorites, this.storageTimeInDays);
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
